import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import Footer from '../components/Footer';

const CAMPUSES = [
  {
    name: 'Par Hoti Campus',
    badge: 'Main Campus',
    badgeBg: 'bg-primary',
    address: 'Nehr Chowk, Swabi Road Mardan',
    phone: '0343-0001144/55/66/77',
    programs: 'All Grades Available',
    hours: 'Mon–Fri: 8:00 AM – 3:00 PM | Sat: 8:00 AM – 12:00 PM',
    mapLink: 'https://maps.google.com/?q=Nehr+Chowk+Swabi+Road+Mardan',
    isMain: true,
  },
  {
    name: 'DHA Campus',
    badge: '27+ Years',
    badgeBg: 'bg-green-600',
    address: 'Muqam Chowk, Mardan',
    phone: '0343-0001133',
    programs: 'Play Group to Grade 5',
    hours: 'Mon–Fri: 8:00 AM – 3:00 PM | Sat: 8:00 AM – 12:00 PM',
    mapLink: 'https://maps.google.com/?q=Muqam+Chowk+Mardan',
    isMain: false,
  },
  {
    name: 'SMT Campus',
    badge: 'Modern',
    badgeBg: 'bg-purple-600',
    address: 'Phase-2 Sector N, House No.1',
    phone: '0343-0001122',
    programs: 'All Grades Available',
    hours: 'Mon–Fri: 8:00 AM – 3:00 PM | Sat: 8:00 AM – 12:00 PM',
    mapLink: 'https://maps.google.com/?q=Phase+2+Sector+N+Mardan',
    isMain: false,
  },
];

const Contact = () => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = (e) => {
    e.preventDefault();
    // In a real deployment, this would send to a backend
    const subject = encodeURIComponent(formData.subject || 'Inquiry from Website');
    const body = encodeURIComponent(`Name: ${formData.name}\nEmail: ${formData.email}\nPhone: ${formData.phone}\n\nMessage:\n${formData.message}`);
    window.location.href = `mailto:info@pakschoolandcollege.com?subject=${subject}&body=${body}`;
    setSubmitted(true);
  };

  return (
    <div className="page-enter">
      <Helmet>
        <title>Contact Us — PAK School &amp; College System | Get In Touch</title>
        <meta name="description" content="Contact PAK School & College System in Mardan. Visit any of our 3 campuses, call us, or send us an email. We're here to help with admissions and inquiries." />
        <meta name="keywords" content="Contact PAK School, School Contact Mardan, Admission Inquiry, School Address, PAK School Phone Number" />
        <link rel="canonical" href="https://pakschoolandcollege.com/contact" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ContactPage",
          "name": "Contact PAK School & College System",
          "url": "https://pakschoolandcollege.com/contact"
        })}</script>
      </Helmet>

      {/* Hero */}
      <div className="page-hero">
        <div className="page-hero-content">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20 text-white/80 text-sm mb-5">
            📞 We're here to help
          </div>
          <h1>Get In <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-accent">Touch</span></h1>
          <p>Have questions about admissions, programs, or campus visits? Our team is ready to assist you.</p>
        </div>
      </div>

      {/* Quick contact bar */}
      <div className="bg-white border-b border-gray-100 py-4">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-6 sm:gap-10">
            <a href="tel:03430001144" className="flex items-center gap-2.5 text-gray-600 hover:text-primary transition-colors group">
              <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center group-hover:bg-primary group-hover:text-white transition-all">
                <i className="fas fa-phone text-sm" />
              </div>
              <span className="font-inter text-sm font-500">0343-0001144</span>
            </a>
            <a href="mailto:info@pakschoolandcollege.com" className="flex items-center gap-2.5 text-gray-600 hover:text-primary transition-colors group">
              <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center group-hover:bg-primary group-hover:text-white transition-all">
                <i className="fas fa-envelope text-sm" />
              </div>
              <span className="font-inter text-sm font-500">info@pakschoolandcollege.com</span>
            </a>
            <div className="flex items-center gap-2.5 text-gray-600">
              <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                <i className="fas fa-map-marker-alt text-sm text-primary" />
              </div>
              <span className="font-inter text-sm font-500">Mardan, KPK, Pakistan</span>
            </div>
          </div>
        </div>
      </div>

      {/* Campuses */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="section-badge mx-auto w-fit">Our Locations</div>
            <h2 className="section-title">Visit Our Campuses</h2>
            <p className="section-subtitle mx-auto">We have three campuses across Mardan for your convenience</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {CAMPUSES.map((campus, i) => (
              <div key={i} className={`bg-white rounded-2xl p-7 shadow-sm border transition-all duration-300 hover:shadow-xl hover:-translate-y-1.5
                ${campus.isMain ? 'border-primary/30 ring-1 ring-primary/20' : 'border-gray-100'}`}>
                <div className="flex items-center justify-between mb-6">
                  <div className="w-12 h-12 rounded-xl bg-gray-50 flex items-center justify-center">
                    <i className="fas fa-school text-gray-400 text-lg" />
                  </div>
                  <span className={`${campus.badgeBg} text-white text-xs font-poppins font-700 px-3 py-1.5 rounded-full`}>{campus.badge}</span>
                </div>
                <h3 className="font-poppins font-700 text-navy text-lg mb-5">{campus.name}</h3>
                <div className="space-y-3.5">
                  <div className="flex items-start gap-3">
                    <i className="fas fa-map-marker-alt text-primary mt-0.5 w-4 flex-shrink-0 text-center" />
                    <p className="text-gray-500 font-inter text-sm">{campus.address}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <i className="fas fa-phone text-accent w-4 flex-shrink-0 text-center" />
                    <a href={`tel:${campus.phone.split('/')[0].replace(/\D/g, '')}`} className="text-gray-500 font-inter text-sm hover:text-accent transition-colors">{campus.phone}</a>
                  </div>
                  <div className="flex items-start gap-3">
                    <i className="fas fa-clock text-green-500 mt-0.5 w-4 flex-shrink-0 text-center" />
                    <p className="text-gray-500 font-inter text-xs leading-relaxed">{campus.hours}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <i className="fas fa-graduation-cap text-purple-500 w-4 flex-shrink-0 text-center" />
                    <p className="text-gray-500 font-inter text-sm">{campus.programs}</p>
                  </div>
                </div>
                <a
                  href={campus.mapLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-5 flex items-center justify-center gap-2 w-full py-2.5 rounded-xl border border-gray-200 text-gray-600 hover:border-primary hover:text-primary text-sm font-poppins font-600 transition-all duration-200"
                >
                  <i className="fas fa-directions" /> Get Directions
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form + Social */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-12">
            {/* Form */}
            <div className="lg:col-span-3">
              <div className="section-badge w-fit">Send a Message</div>
              <h2 className="section-title mb-2">We'd Love to Hear From You</h2>
              <p className="text-gray-500 font-inter text-sm mb-7">Fill out the form below and our team will get back to you within 24 hours.</p>

              {submitted ? (
                <div className="bg-green-50 border border-green-200 rounded-2xl p-8 text-center">
                  <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                    <i className="fas fa-check-circle text-green-600 text-2xl" />
                  </div>
                  <h3 className="font-poppins font-700 text-green-800 text-lg mb-2">Message Sent!</h3>
                  <p className="text-green-600 text-sm font-inter">Thank you for reaching out. We'll get back to you soon.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-navy font-poppins font-600 text-sm mb-1.5" htmlFor="name">Full Name *</label>
                      <input
                        id="name" name="name" type="text" required
                        value={formData.name} onChange={handleChange}
                        placeholder="Your full name"
                        className="w-full px-4 py-3 rounded-xl border border-gray-200 text-navy text-sm font-inter focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-navy font-poppins font-600 text-sm mb-1.5" htmlFor="phone">Phone Number</label>
                      <input
                        id="phone" name="phone" type="tel"
                        value={formData.phone} onChange={handleChange}
                        placeholder="0300-0000000"
                        className="w-full px-4 py-3 rounded-xl border border-gray-200 text-navy text-sm font-inter focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-navy font-poppins font-600 text-sm mb-1.5" htmlFor="email">Email Address *</label>
                    <input
                      id="email" name="email" type="email" required
                      value={formData.email} onChange={handleChange}
                      placeholder="your@email.com"
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 text-navy text-sm font-inter focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-navy font-poppins font-600 text-sm mb-1.5" htmlFor="subject">Subject *</label>
                    <select
                      id="subject" name="subject" required
                      value={formData.subject} onChange={handleChange}
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 text-gray-600 text-sm font-inter focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all bg-white"
                    >
                      <option value="">Select a subject</option>
                      <option value="Admission Inquiry">Admission Inquiry</option>
                      <option value="Academic Information">Academic Information</option>
                      <option value="Fee Structure">Fee Structure</option>
                      <option value="Campus Visit">Campus Visit</option>
                      <option value="General Inquiry">General Inquiry</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-navy font-poppins font-600 text-sm mb-1.5" htmlFor="message">Message *</label>
                    <textarea
                      id="message" name="message" rows={5} required
                      value={formData.message} onChange={handleChange}
                      placeholder="Tell us how we can help you..."
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 text-navy text-sm font-inter focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all resize-none"
                    />
                  </div>
                  <button
                    type="submit"
                    className="w-full py-3.5 bg-primary hover:bg-primary-dark text-white font-poppins font-700 text-sm rounded-xl transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-primary/25 flex items-center justify-center gap-2"
                  >
                    <i className="fas fa-paper-plane" />
                    Send Message
                  </button>
                </form>
              )}
            </div>

            {/* Info sidebar */}
            <div className="lg:col-span-2 space-y-5">
              <div>
                <div className="section-badge w-fit">Stay Connected</div>
                <h2 className="font-poppins font-700 text-navy text-2xl mb-5">Other Ways to Reach Us</h2>
              </div>

              {[
                { icon: 'fa-phone', label: 'Main Phone', value: '0343-0001144/55/66/77', href: 'tel:03430001144', color: 'bg-primary/10 text-primary' },
                { icon: 'fa-envelope', label: 'Email', value: 'info@pakschoolandcollege.com', href: 'mailto:info@pakschoolandcollege.com', color: 'bg-accent/10 text-accent' },
                { icon: 'fa-map-marker-alt', label: 'Main Address', value: 'Nehr Chowk, Swabi Road Mardan, KPK', href: null, color: 'bg-green-100 text-green-600' },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-4 p-4 bg-gray-50 rounded-2xl hover:bg-gray-100 transition-colors">
                  <div className={`w-11 h-11 rounded-xl ${item.color} flex items-center justify-center flex-shrink-0`}>
                    <i className={`fas ${item.icon}`} />
                  </div>
                  <div>
                    <p className="text-gray-400 font-inter text-xs mb-0.5">{item.label}</p>
                    {item.href ? (
                      <a href={item.href} className="text-navy font-inter font-500 text-sm hover:text-primary transition-colors">{item.value}</a>
                    ) : (
                      <p className="text-navy font-inter font-500 text-sm">{item.value}</p>
                    )}
                  </div>
                </div>
              ))}

              {/* Social media */}
              <div className="pt-3">
                <p className="font-poppins font-600 text-navy text-sm mb-4">Follow Us on Social Media</p>
                <div className="flex gap-3">
                  <a
                    href="https://www.facebook.com/pakschoolssystem"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-poppins font-600 hover:bg-blue-700 transition-colors"
                    aria-label="Facebook"
                  >
                    <i className="fab fa-facebook-f" /> Facebook
                  </a>
                  <a
                    href="https://www.instagram.com/pakschoolcollege_mardan"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl text-sm font-poppins font-600 hover:opacity-90 transition-opacity"
                    aria-label="Instagram"
                  >
                    <i className="fab fa-instagram" /> Instagram
                  </a>
                </div>
              </div>

              {/* Office hours */}
              <div className="bg-navy/5 rounded-2xl p-5 mt-2">
                <h4 className="font-poppins font-700 text-navy text-sm mb-3 flex items-center gap-2">
                  <i className="fas fa-clock text-primary" /> Office Hours
                </h4>
                <div className="space-y-2 text-sm font-inter text-gray-600">
                  <div className="flex justify-between">
                    <span>Monday – Friday</span>
                    <span className="font-600 text-navy">8:00 AM – 3:00 PM</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Saturday</span>
                    <span className="font-600 text-navy">8:00 AM – 12:00 PM</span>
                  </div>
                  <div className="flex justify-between text-gray-400">
                    <span>Sunday</span>
                    <span>Closed</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Contact;
